import type { IChatListItem } from '@/apiClient/types/chat'

import {
  EXPIRING_SOON_LABEL,
  RECENT_LABEL,
  getChatDeletionInfo,
} from './chatExpiry'

/**
 * Buckets sidebar chat list items into Today / Yesterday / Last 7 days / Recent, and pulls any
 * chat within the deletion window into an "Expiring soon" bucket (rendered last). New streaming
 * chats are prepended into Today (deduped against API "today" rows); empty buckets are dropped.
 */
export function groupChatsByRelativeDate(
  chatsFromList: IChatListItem[],
  newChatItems: IChatListItem[],
  now: Date = new Date(),
): Map<string, IChatListItem[]> {
  const result = new Map<string, IChatListItem[]>([
    ['Today', []],
    ['Yesterday', []],
    ['Last 7 days', []],
    [RECENT_LABEL, []],
    [EXPIRING_SOON_LABEL, []],
  ])

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(today.getDate() - 7)

  const thirtyDaysAgo = new Date(today)
  thirtyDaysAgo.setDate(today.getDate() - 30)

  const newChatIds = new Set(newChatItems.map((item) => item.id))

  chatsFromList.forEach((chat) => {
    if (!chat.updated_at) return

    // Expiring-soon chats surface only in their own section (never double-listed in a date
    // bucket). Never divert a brand-new chat the user is actively streaming into.
    const deletionInfo = getChatDeletionInfo(chat, now)
    if (deletionInfo?.isExpiringSoon && !newChatIds.has(chat.id)) {
      result.get(EXPIRING_SOON_LABEL)?.push(chat)
      return
    }

    const chatDate = new Date(chat.updated_at * 1000)
    const chatDateOnly = new Date(
      chatDate.getFullYear(),
      chatDate.getMonth(),
      chatDate.getDate(),
    )

    if (chatDateOnly <= thirtyDaysAgo) return

    if (chatDateOnly.getTime() === today.getTime()) {
      result.get('Today')?.push(chat)
    } else if (chatDateOnly.getTime() === yesterday.getTime()) {
      result.get('Yesterday')?.push(chat)
    } else if (chatDateOnly >= sevenDaysAgo && chatDateOnly < yesterday) {
      result.get('Last 7 days')?.push(chat)
    } else {
      result.get(RECENT_LABEL)?.push(chat)
    }
  })

  const todayFromApi = result.get('Today') ?? []
  const todayWithoutNewChatDupes = todayFromApi.filter(
    (listChat) => !newChatIds.has(listChat.id),
  )
  result.set('Today', [...newChatItems, ...todayWithoutNewChatDupes])

  for (const [key, value] of result.entries()) {
    if (value.length === 0) {
      result.delete(key)
    }
  }

  return result
}
